import React, { useMemo } from "react";

import { Transfer } from "@prequel/react";
import {
  Badge,
  BadgeColor,
  CopyToClipboard,
  EventAvailableIcon,
  EventIcon,
  EventNoteIcon,
  IntegrationInstructionsIcon,
  LogInIcon,
  LogOutIcon,
  RefreshIcon,
  RotateRightIcon,
  Table,
  TableCellType,
  TagIcon,
  UserIcon,
} from "@prequel/shared-components";

import { useUsers } from "@/api/queries/users";
import Timestamp from "@/components/Timestamp";
import TransferStatusBadge, {
  getModelDisplayStatus,
  getTransferStatusBadgeColor,
  isTransferStatusBadgeActive,
  TransferStatus,
} from "@/components/TransferStatusBadge";

const COLUMNS = ["Model Name", "Status", "Rows Transferred", "Delta Rows Transferred", "Max Last_Updated", "Error"];

const SectionItem = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) => (
  <div className="grid grid-cols-8 items-start gap-x-4 gap-y-2">
    <div className="text-ashy flex items-center">
      {icon}
      <span className="icon-class mr-2"></span>
      {label}
    </div>
    <div className="col-span-3 mt-0.5 flex text-sm">{value}</div>
  </div>
);

const SectionContent = ({ transfer }: TransferDetailsSectionProps) => {
  const { data: users } = useUsers();

  const transferStatusProps = useMemo(() => {
    const status = transfer.status as TransferStatus;
    return {
      status,
      color: getTransferStatusBadgeColor(status),
      isActive: isTransferStatusBadgeActive(status),
    };
  }, [transfer.status]);

  const triggeredBy = useMemo(() => {
    const { actor_type, actor_id } = transfer;

    if (actor_type === "user") {
      const user = users?.find((u) => u.id === actor_id);
      return user?.email ?? actor_id ?? "Unknown";
    }

    if (actor_type === "api_key") {
      return "API";
    }

    if (actor_type === "service" && actor_id === "scheduler") {
      return "Scheduler";
    }

    return "Unknown";
  }, [transfer, users]);

  const renderedTags = useMemo(() => {
    if (!transfer.tags || Object.entries(transfer.tags).length === 0) {
      return "N/A";
    }

    return (
      <div className="flex flex-wrap items-center gap-1">
        {Object.entries(transfer.tags).map(([key, value]) => (
          <Badge key={key} text={`${key}: ${value}`} color={BadgeColor.GRAY} className="mr-1" />
        ))}
      </div>
    );
  }, [transfer.tags]);

  return (
    <div className="space-y-4 py-4">
      <SectionItem
        icon={<IntegrationInstructionsIcon />}
        label="Job id"
        value={
          <>
            <CopyToClipboard textToCopy={transfer.id} />
            <span className="rounded-sm bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600">
              <span className="mr-2">{transfer.id}</span>
            </span>
          </>
        }
      />
      <SectionItem
        icon={<RotateRightIcon />}
        label="Status"
        value={
          <TransferStatusBadge
            status={transferStatusProps.status}
            color={transferStatusProps.color}
            isActive={transferStatusProps.isActive}
          />
        }
      />
      <SectionItem
        icon={<RefreshIcon />}
        label="Type"
        value={
          <Badge
            text={transfer.is_full_refresh ? "Full refresh" : "Incremental"}
            color={transfer.is_full_refresh ? BadgeColor.ORANGE : BadgeColor.GRAY}
          />
        }
      />
      <SectionItem icon={<EventIcon />} label="Submitted" value={<Timestamp timestamp={transfer.submitted_at} />} />
      <SectionItem
        icon={<EventNoteIcon />}
        label="Started"
        value={transfer.started_at ? <Timestamp timestamp={transfer.started_at} /> : "N/A"}
      />
      <SectionItem
        icon={<EventAvailableIcon />}
        label="Completed"
        value={transfer.ended_at ? <Timestamp timestamp={transfer.ended_at} /> : "N/A"}
      />
      {transfer.start_transfer_window_at && (
        <SectionItem
          icon={<LogOutIcon />}
          label="Window Start"
          value={<Timestamp timestamp={transfer.start_transfer_window_at} />}
        />
      )}
      {transfer.end_transfer_window_at && (
        <SectionItem
          icon={<LogInIcon />}
          label="Window End"
          value={<Timestamp timestamp={transfer.end_transfer_window_at} />}
        />
      )}
      <SectionItem
        icon={<UserIcon />}
        label="Triggered by"
        value={<Badge text={triggeredBy} color={BadgeColor.GRAY} />}
      />
      <SectionItem icon={<TagIcon />} label="Tags" value={renderedTags} />
    </div>
  );
};

type TransferDetailsSectionProps = {
  transfer: Transfer;
};

const TransferDetailsSection = ({ transfer }: TransferDetailsSectionProps) => {
  const modelRows = useMemo(() => {
    // Sort models alphabetically by name first
    const sortedModels = [...transfer.models].sort((a, b) => a.localeCompare(b));

    return sortedModels.map((model) => {
      const metric = transfer.model_metrics?.find((m) => m.model_name === model);
      const displayStatus = getModelDisplayStatus(transfer.status, metric);
      return {
        id: model,
        cells: [
          { value: model },
          {
            value: <TransferStatusBadge status={displayStatus} />,
          },
          {
            value: (metric?.rows_transferred ?? 0).toLocaleString(),
          },
          {
            value: (metric?.delta_rows_transferred ?? 0).toLocaleString(),
          },
          {
            value:
              metric && metric.most_recent_last_updated_at ? (
                <Timestamp timestamp={metric.most_recent_last_updated_at} localized={false} />
              ) : (
                "N/A"
              ),
          },
          {
            type: TableCellType.OVERFLOW,
            value: metric?.error ? <div className="min-w-[350px] font-mono text-xs">{metric.error.trace}</div> : "",
          },
        ],
      };
    });
  }, [transfer.models, transfer.model_metrics, transfer.status]);

  return (
    <div>
      <SectionContent transfer={transfer} />
      <h2 className="mt-8 mb-4 text-xl font-semibold">Model Summary ({modelRows.length})</h2>
      <Table columns={COLUMNS} rows={modelRows} footer={`${modelRows.length} models`} />
    </div>
  );
};

export default TransferDetailsSection;
